import React, { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container, Sprite } from "@pixi/react";
import { useSelector } from "react-redux";
import ReelFrontController, {
  STATIC_CONFIG,
  SPINE_CONFIG,
} from "./ReelFrontController";
import { Settings } from "../../../settings";
import { selectHoldAndWinActive } from "../../../State/selectors";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import { useSlotRegistry } from "../../../Contexts/SlotRegistryContext";

const ReelFront = ({
  symbolWidth,
  symbolHeight,
  dimensions,
  dividerDimensions,
  renderMode = "spine",
}) => {
  const { t } = useTranslation();
  const reelFrontControllerRef = useRef(null);
  const spriteRef = useRef(null);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const slotRegistry = useSlotRegistry();

  useEffect(() => {
    if (
      renderMode === "spine" &&
      spriteRef.current &&
      !reelFrontControllerRef.current
    ) {
      reelFrontControllerRef.current = new ReelFrontController(
        spriteRef.current,
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
        t,
        slotRegistry,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode]);

  // Handle dimension changes for responsive design (spine mode only)
  useEffect(() => {
    if (renderMode === "spine" && reelFrontControllerRef.current) {
      reelFrontControllerRef.current.resizeReelFront(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight, renderMode]);

  // Memoize divider count - constant value
  const dividerCount = useMemo(() => Settings.REEL_COUNT - 1, []);

  // Memoize divider width
  const dividerWidth = useMemo(
    () => dividerDimensions?.width ?? 0,
    [dividerDimensions?.width],
  );

  // Always use base config (decorations are now separate component)
  // Config is constant, no need to memoize
  const config = STATIC_CONFIG;

  // Memoize static dimensions calculations
  const staticWidth = useMemo(
    () =>
      (symbolWidth * Settings.REEL_COUNT + dividerWidth * dividerCount) *
      config.widthMultiplier,
    [symbolWidth, dividerWidth, dividerCount, config.widthMultiplier],
  );

  const staticHeight = useMemo(
    () => symbolHeight * Settings.SYMBOLS_VISIBLE * config.heightMultiplier,
    [symbolHeight, config.heightMultiplier],
  );

  // Memoize texture lookup for static mode
  const staticTexture = useMemo(() => {
    if (renderMode !== "static") return null;
    return PIXI.Assets.get(ASSET_NAME.reel_frame_front_static);
  }, [renderMode]);

  // Memoize container position calculations
  const containerPosition = useMemo(() => {
    if (renderMode === "spine") {
      return {
        x: symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.xOffset,
        y:
          -symbolHeight *
          Settings.SYMBOLS_VISIBLE *
          SPINE_CONFIG.yOffset,
      };
    }
    return {
      x: config.xOffset,
      y: config.yOffset,
    };
  }, [
    renderMode,
    symbolWidth,
    symbolHeight,
    config.xOffset,
    config.yOffset,
  ]);

  // Memoize zIndex calculation
  const containerZIndex = useMemo(
    () => (holdAndWinActive ? 6 : 4),
    [holdAndWinActive],
  );

  return (
    <Container
      y={containerPosition.y}
      x={containerPosition.x}
      ref={spriteRef}
      name="front_texture"
      zIndex={containerZIndex}
      sortableChildren={true}
    >
      

      {renderMode === "static" && staticTexture && (
        <Sprite
          texture={staticTexture}
          width={staticWidth}
          height={staticHeight}
          zIndex={4}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default ReelFront;
